/*
 * The admin site's words in English: the articles section (S48, extended to the
 * admin site 2026-10-29) - the article list, the new-article form, the editor
 * and its cards. Every line is the text those components showed before, moved
 * here unchanged.
 *
 * Three of the cards serve more than articles: PublishControls and the history
 * serve release notes, and the delete dialog every kind of thing an
 * administrator deletes. Their sentences that name the thing take it - as the
 * `noun` the panel is given ('article' / 'release note') or the delete's `kind` -
 * and each language builds its own whole sentence from it.
 */

import type { AdminArticleStatusFilter, DeleteTargetKind, ImpactEffect } from '@accotool-kb/shared';

/** "1 link", "2 links": the English plural, for the lines that count. */
function count(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** What the delete dialog's title calls each kind. */
const KIND: Record<DeleteTargetKind, string> = {
  article: 'article',
  'release-note': 'release note',
  product: 'product',
  category: 'category',
};

export const articlesEn = {
  /** Words several of the article forms and cards share. */
  shared: {
    articles: 'Articles',
    untitled: '(untitled)',
    title: 'Title',
    slug: 'Slug',
    categoryTags: 'Category tags',
    properties: 'Properties',
    publishing: 'Publishing',
    publishAtLocal: 'Publish at (your local time)',
    saving: 'Saving…',
    saveAndPublish: 'Save & publish',
    unsavedChanges: 'Unsaved changes',
    cancel: 'Cancel',
    draft: 'draft',
    live: 'live',
    titleRequired: 'A title is required.',
    categoryRequired: 'Assign at least one category tag.',
  },

  /** The article list (ArticleListPage.tsx). */
  list: {
    matching: (total: number) => `${total} matching`,
    newArticle: 'New article',
    /** After a delete: the server's own description of each step follows. */
    deleted: (label: string, steps: string) => `Deleted "${label}". ${steps}`,
    search: 'Search',
    searchPlaceholder: 'Title, slug or body text…',
    state: 'State',
    /** The saved views, by the filter value the server parses. */
    status: {
      all: 'All articles',
      live: 'Live',
      scheduled: 'Scheduled',
      hidden: 'Hidden',
      draft: 'Unpublished draft',
      withdrawn: 'Text withdrawn',
      unwritten: 'Not written yet',
      untagged: 'Untagged',
    } satisfies Record<AdminArticleStatusFilter, string>,
    product: 'Product',
    category: 'Category',
    any: 'Any',
    sort: 'Sort',
    sortModified: 'Last edited',
    sortCreated: 'Newest',
    sortPublish: 'Publish date',
    sortTitle: 'Title',
    loading: 'Loading articles…',
    noneMatch: 'No articles match',
    noneMatchHint: 'Change the filters, or create the first article.',
    columnArticle: 'Article',
    columnState: 'State',
    columnPublish: 'Publish',
    columnEdited: 'Edited',
    columnActions: 'Actions',
    needsCategory: 'needs a category',
    /** A private article's audience whose key the server no longer configures. */
    audienceNotConfigured: (key: string) => `${key} (not configured)`,
    draftTitle: (latest: number | null, current: number | null) =>
      `Revision ${latest} is newer than the published revision ${current ?? '—'}.`,
    edit: 'Edit',
    delete: 'Delete',
  },

  /** Creating an article (NewArticlePage.tsx). */
  create: {
    loadingMetadata: 'Loading metadata…',
    setUpMetadata: 'Set up the metadata first',
    setUpMetadataBody:
      'An article needs a product - it is half of the address - and at least one category tag. Some of those do not exist yet.',
    noProductTags: 'No product tags.',
    noCategoryTags: 'No category tags.',
    goToMetadata: 'Go to Metadata',
    breadcrumbNew: 'New',
    heading: 'New article',
    intro: 'The article is created with its first revision, so it has something to show the moment it is published.',
    content: 'Content',
    slugRequired: 'A slug is required. Type one if the title has no letters or digits in it.',
    productRequired: 'Assign a product.',
    slugHint:
      'Derived from the title until you edit it. A number is appended if the chosen product already answers on it.',
    whenVisible: 'When should readers see it',
    modeHidden: 'Keep it hidden for now',
    modeNow: 'Publish immediately',
    modeSchedule: 'Schedule for later',
    hintHidden: 'Nothing is visible to readers until you publish it.',
    hintNow: 'Readable as soon as it is created.',
    hintSchedule: 'Becomes readable at the chosen instant.',
    /** The UTC instant the local time will be stored as. */
    storedAs: (utc: string) => `Stored as ${utc}`,
    creating: 'Creating…',
    createArticle: 'Create article',
  },

  /** The article editor's page, its saves and its Text card (ArticleEditorPage.tsx). */
  editor: {
    deleted: 'Deleted',
    deletedBody: (label: string) => `“${label}” and everything belonging to it has been deleted.`,
    backToArticles: 'Back to articles',
    /** {created} and {modified} are dates. */
    createdLine: 'Created {created} · last activity {modified}',
    viewOnSite: 'View on site',
    deleteArticle: 'Delete article',
    savedAndPublished: (revision: number) => `Revision ${revision} saved and published.`,
    savedAsDraft: (revision: number, readersSee: number | null) =>
      `Revision ${revision} saved as a draft. Readers still see revision ${readersSee ?? '—'}.`,
    outOfDate: 'Your copy is out of date.',
    outOfDateHelp:
      'Copy your text somewhere safe, reload the page, and re-apply your change on top of theirs. Nothing has been overwritten.',
    text: 'Text',
    draftBadge: (revision: number | null) => `draft #${revision ?? ''}`,
    nothingWritten: 'nothing written yet',
    editingDraft: (latest: number | null, current: number | null) =>
      `You are editing draft revision ${latest ?? ''}; readers are getting revision ${current ?? '—'}.`,
    editingDraftHelp: 'Save & publish to make this the version readers see.',
  },

  /** The Properties card (ArticleProperties.tsx). */
  properties: {
    slugHint:
      'Unique within the product, not across the knowledge base: another product may answer on the same slug.',
    slugTaken: 'Already used by another article in this product. Choose a different slug.',
    addressChanges: 'Changing the product or the slug changes the address.',
    linksBeingChecked:
      'Which other articles link here by the current address is still being checked; any that do are rewritten to the new one when you save, each as a new revision.',
    linksWillBeRewritten: (articles: number) =>
      `${count(articles, 'other article')} ${articles === 1 ? 'links' : 'link'} here by the current address; those links are rewritten to the new one when you save, each as a new revision.`,
    /** {token} is the token syntax, as code. */
    tokensKeepWorking: 'Links written with {token} tokens keep working.',
    /** {address} is the article's current address, as code. */
    handWrittenLinks: 'Hand-written links to {address} are not updated.',
    save: 'Save properties',
    /** The notice after a save that moved the address - its sentences in order. */
    addressIsNow: (address: string) => `The address is now ${address}.`,
    linksRenamed: (links: number, articles: number, releaseNotes: number, revisions: number, previous: string) => {
      const holders =
        releaseNotes > 0
          ? articles > 0
            ? `${count(articles, 'other article')} and ${count(releaseNotes, 'release note')}`
            : count(releaseNotes, 'release note')
          : count(articles, 'other article');
      return (
        `${count(links, 'link')} in ${holders} pointed at ${previous} and now point here ` +
        `(${count(revisions, 'new revision')} appended).`
      );
    },
    noLinksRenamed: 'No other article linked here by the old address.',
    draftLinksRenamed: (links: number) =>
      `${count(links, 'link')} to this article's own old address in the text below ` +
      `${links === 1 ? 'was' : 'were'} updated too; save the text to keep that.`,
  },

  /** The Article card: the change note and the two saves (ArticleSaveBox.tsx). */
  saveBox: {
    heading: 'Article',
    placeholder: 'e.g. Updated the screenshots for the new portal',
    changeNote: 'Change note',
    saveAndPublishTitle: 'Append a revision and make it the one readers get.',
    saveAsDraft: 'Save as draft',
    saveAsDraftTitle: 'Append a revision without changing what readers see.',
  },

  /** The Publishing card (PublishControls.tsx). `noun` is 'article' or 'release note'. */
  publishing: {
    chooseDateTime: 'Choose a date and time.',
    /** {date} is the stored publish instant. */
    publishDate: 'Publish date and time: {date}',
    publishDateScheduled: 'Publish date and time: {date} — in the future, so nothing is readable yet.',
    unwritten: 'Set to be published, but nothing has been written yet.',
    unwrittenHelp: (noun: string) =>
      `Readers see nothing until a revision is published. Write the ${noun} below and use Save & publish.`,
    publishNow: 'Publish immediately',
    publishNowTitle: (noun: string) => `Set the publish date to now and make the ${noun} visible.`,
    hide: 'Hide from readers',
    hideTitle: 'Hide from everyone except administrators. Reversible, and the history is kept.',
    scheduleLegend: 'Schedule',
    howStored: 'How the publish date is stored and evaluated',
    howStoredBody:
      'Stored as UTC. Scheduling is evaluated against the database clock, so it does not shift with daylight saving.',
    alreadyPassed: (noun: string) =>
      `That instant has already passed, so the ${noun} becomes readable as soon as you save.`,
    setPublishDate: 'Set publish date',
    schedule: 'Schedule',
  },

  /** The History card and its two dialogs (RevisionHistoryPanel.tsx). */
  history: {
    loading: 'Loading history…',
    heading: 'History',
    count: (revisions: number) => count(revisions, 'revision'),
    empty: 'Nothing has been written yet. Saving will create revision 1.',
    characters: (characters: number) => `${characters.toLocaleString()} characters`,
    aheadOfLive: 'Newer than the revision readers get.',
    view: 'View',
    compare: 'Compare with live',
    promote: 'Publish this',
    promoteTitle: 'Make this the revision readers get. Takes effect immediately.',
    restore: 'Copy to new draft',
    restoreTitle: 'Append a copy of this revision as a new draft. History is not rewritten.',
    viewerTitle: (revision: number, title: string) => `Revision ${revision} — ${title}`,
    diffTitle: (live: number, other: number) => `Revision ${live} (live) compared with revision ${other}`,
    diffSummary: (added: number, removed: number, approximate: boolean) =>
      `${count(added, 'line')} added, ${removed} removed${
        approximate ? ' (approximate: the revisions are very large)' : ''
      }`,
    /** {from} and {to} are the two titles, in bold. */
    titleDiffers: 'The title differs: {from} → {to}',
  },

  /** Who may read the article (VisibilityField.tsx). */
  visibility: {
    label: 'Visibility',
    publicHint: 'Everybody can read it once it is published.',
    privateHint:
      'Once it is published, only the audiences ticked below and the administrators can read it - signed in.',
    privateUnavailable: 'No audiences are configured on this server, so it cannot be made private.',
    audiences: 'Audiences',
    loadingAudiences: 'Loading the audiences…',
    noAudiences:
      'No audiences are configured on this server. Make the article public, or ask for one to be configured.',
    /** `keys` is the list of keys, comma-separated. */
    noLongerConfigured: (keys: string) => `No longer configured, and removed when you save: ${keys}.`,
    /** The server's own words for "private, nothing ticked". */
    audienceRequired: 'Choose at least one audience, or make the article public.',
  },

  /** The product radios and the category checkboxes (ProductSelect.tsx, TagMultiSelect.tsx). */
  tags: {
    product: 'Product',
    noTags: (kind: 'product' | 'category') => `No ${kind} tags exist yet. Create one under Metadata.`,
    retired: '(retired)',
  },

  /** The Linked from card (LinkedFromPanel.tsx). */
  linkedFrom: {
    heading: 'Linked from',
    /** {token} is the token syntax, as code. */
    intro: (holders: 'articles' | 'releaseNotes' | 'both') =>
      `${
        holders === 'both'
          ? 'These articles and release notes reference'
          : holders === 'releaseNotes'
            ? 'These release notes reference'
            : 'These articles reference'
      } this one with {token} tokens. Deleting this article rewrites them.`,
    articles: 'Articles',
    releaseNotes: 'Release notes',
    revision: (revision: number, isCurrent: boolean) =>
      `(revision ${revision}, ${isCurrent ? 'live' : 'draft'})`,
  },

  /** The delete confirmation (DeleteImpactDialog.tsx), for every kind it deletes. */
  deleteDialog: {
    title: (kind: DeleteTargetKind, name: string) => `Delete ${KIND[kind]}: ${name}`,
    /** Who does each consequence: the database, this application, or nobody. */
    effect: {
      cascade: 'database cascade',
      cleanup: 'cleaned up',
      rewrite: 'text rewritten',
      blocked: 'blocks the delete',
      none: 'for information',
    } satisfies Record<ImpactEffect, string>,
    nothingChangedYet: 'Nothing has been changed yet.',
    cannotDelete: 'This cannot be deleted as things stand.',
    deleting: 'Deleting…',
    deletePermanently: 'Delete permanently',
    working: 'Working out what this would affect…',
    choose: 'Choose…',
    whatWillHappen: 'What will happen',
    /** {name} is the name that has to be typed, in bold. */
    typeToConfirm: 'Type {name} to confirm',
    typeToConfirmHint: 'This delete destroys version history that cannot be recovered.',
    /** `total` only when the list was cut short. */
    showItems: (shown: number, total: number | null) => `Show ${shown}${total === null ? '' : ` of ${total}`}`,
    moreNotListed: (more: number) => `${more} more not listed.`,
  },
};
